import React from 'react';
import { DNABox, DNAButton, DNAText, Iffy, ProgressBar } from '@alucio/lux-ui';
import {
  CRMAccountSyncCheckpoint,
  CRMAccountSyncStatus,
} from 'src/classes/CRM/CRMIndexedDBTypes';

type CRMAccountSyncProgressProps = {
  accountSync?: CRMAccountSyncCheckpoint;
  onRetry: () => void;
};

const formatCount = (count: number) => new Intl.NumberFormat('en-US').format(count);

export const CRMAccountSyncProgress: React.FC<CRMAccountSyncProgressProps> = ({
  accountSync,
  onRetry,
}) => {
  if (
    !accountSync ||
    accountSync.status === CRMAccountSyncStatus.IDLE ||
    accountSync.status === CRMAccountSyncStatus.COMPLETED
  ) {
    return null;
  }

  const total = accountSync.total || Math.max(accountSync.syncedCount, 1);
  const progress = Math.min(accountSync.syncedCount, total);
  const progressText = accountSync.total
    ? `${formatCount(accountSync.syncedCount)} of ${formatCount(accountSync.total)}`
    : formatCount(accountSync.syncedCount);
  const isPaused = accountSync.status === CRMAccountSyncStatus.ERROR;
  const message = isPaused
    ? `Account sync paused at ${progressText}.`
    : `Synchronizing CRM accounts: ${progressText}.`;
  const accessibilityProps = { accessibilityRole: 'progressbar' as const };

  return (
    <DNABox
      appearance="col"
      spacing="xs"
      testID="crm-account-sync-progress"
      {...accessibilityProps}
    >
      <DNABox alignY="center" spacing="sm">
        <DNAText c2 status={isPaused ? 'danger' : 'tertiary'}>
          {message}
        </DNAText>
        <Iffy is={isPaused}>
          <DNAButton
            appearance="ghost"
            status="danger"
            size="sm"
            onPress={onRetry}
            testID="crm-account-sync-retry"
          >
            Retry
          </DNAButton>
        </Iffy>
      </DNABox>
      <ProgressBar finalValue={total} progress={progress} />
    </DNABox>
  );
};
